// Canonical role taxonomy. Locked 2026-05-24.
//
// The DB CHECK constraint on profiles.role (see
// supabase/migrations/20260524000001_collapse_role_taxonomy.sql) enforces
// these three values. This module is the single source of truth for the
// client mapping.
//
// Legacy values that may still exist in callers, route guards, or pre-CHECK
// rows are collapsed here:
//   TEAMLEDER / SAGSBEHANDLER / CASE_WORKER / COORDINATOR  → KOMMUNE
//   SHIFT_WORKER                                            → VIKAR
//   SUPER_ADMIN                                             → ADMIN

export const APP_ROLES = ['VIKAR', 'KOMMUNE', 'ADMIN'] as const;
export type AppRole = (typeof APP_ROLES)[number];

export type LegacyRole =
  | AppRole
  | 'CASE_WORKER'
  | 'TEAMLEDER'
  | 'SAGSBEHANDLER'
  | 'COORDINATOR'
  | 'SHIFT_WORKER'
  | 'SUPER_ADMIN'
  | string
  | null
  | undefined;

const LEGACY_TO_CANONICAL: Record<string, AppRole> = {
  ADMIN: 'ADMIN',
  SUPER_ADMIN: 'ADMIN',
  KOMMUNE: 'KOMMUNE',
  CASE_WORKER: 'KOMMUNE',
  TEAMLEDER: 'KOMMUNE',
  SAGSBEHANDLER: 'KOMMUNE',
  COORDINATOR: 'KOMMUNE',
  KOMMUNEMEDARBEJDER: 'KOMMUNE',
  SOCIAL_WORKER: 'KOMMUNE',
  VIKAR: 'VIKAR',
  SHIFT_WORKER: 'VIKAR',
  MEDARBEJDER: 'VIKAR',
};

/**
 * Normalize any legacy or arbitrary role string to one of the three
 * canonical values. Unknown inputs collapse to VIKAR — the least
 * privileged role — matching the existing fallback behaviour of
 * authUtils.ts:roleMap.
 */
export function normalizeRole(role: LegacyRole): AppRole {
  if (!role) return 'VIKAR';
  const upper = String(role).toUpperCase().trim();
  return LEGACY_TO_CANONICAL[upper] ?? 'VIKAR';
}

/**
 * Expand a guard role list to also include the legacy synonyms, so callers
 * that still pass ["KOMMUNE","CASE_WORKER"] (or older DB rows that haven't
 * been backfilled yet) keep working until the cleanup migration lands.
 *
 * Returns canonical roles only. Use this in `<Guard roles={...}>` so the
 * guard compares apples to apples.
 */
export function expandRoles(roles: ReadonlyArray<string>): AppRole[] {
  const set = new Set<AppRole>();
  for (const r of roles) {
    set.add(normalizeRole(r));
  }
  return Array.from(set);
}
